import { motion } from 'framer-motion';
import battleIcon from '@/assets/bottom-navbar/battle.webp';

interface VerificationLoadingModalProps {
  isOpen: boolean;
}

export const VerificationLoadingModal = ({
  isOpen,
}: VerificationLoadingModalProps) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center">
      <motion.div
        className="w-full max-w-[var(--app-max-width)] h-screen mx-auto bg-black/50 backdrop-blur-md flex items-center justify-center overflow-hidden relative"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div className="flex flex-col items-center gap-6 relative px-4 z-10">
          {/* Shield Icon */}
          <div className="relative w-24 h-24 flex items-center justify-center">
            {/* Shield */}
            <div className="relative z-10">
              <motion.svg
                width="80"
                height="80"
                viewBox="0 0 100 100"
                animate={{
                  scale: [1, 1.05, 1],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              >
                <defs>
                  <linearGradient
                    id="shieldGradient"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="100%"
                  >
                    <stop offset="0%" stopColor="#8B5CF6" />
                    <stop offset="50%" stopColor="#A855F7" />
                    <stop offset="100%" stopColor="#06B6D4" />
                  </linearGradient>
                </defs>

                {/* Shield body */}
                <motion.path
                  d="M50 15 L25 27 L25 50 Q25 70 50 85 Q75 70 75 50 L75 27 Z"
                  stroke="white"
                  strokeWidth="2"
                  fill="url(#shieldGradient)"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1, ease: 'easeInOut' }}
                />
              </motion.svg>

              {/* Battle icon inside shield */}
              <motion.img
                src={battleIcon}
                alt="Battle"
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-7 h-7 object-contain z-20 brightness-110 contrast-125"
                initial={{ opacity: 0, scale: 0 }}
                animate={{
                  opacity: 1,
                  scale: [1, 1.05, 1],
                }}
                transition={{
                  opacity: { duration: 0.5, delay: 0.5 },
                  scale: { duration: 2, repeat: Infinity, ease: 'easeInOut' },
                }}
              />
            </div>

            {/* Spinning gradient ring */}
            <motion.div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-28 h-28 rounded-full"
              style={{
                background:
                  'conic-gradient(from 0deg, transparent 0%, #06B6D4 25%, #A855F7 50%, #FFE500 75%, transparent 100%)',
                maskImage:
                  'radial-gradient(circle, transparent 65%, black 67%, black 100%)',
                WebkitMaskImage:
                  'radial-gradient(circle, transparent 65%, black 67%, black 100%)',
              }}
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            />
          </div>

          <motion.div
            className="flex flex-col items-center gap-3"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <motion.p
              className="text-white text-2xl font-normal text-center"
              style={{
                WebkitTextStroke: '1px #000',
                textShadow: '1px 1px 0px #000',
              }}
              animate={{
                opacity: [0.7, 1, 0.7],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            >
              Securing Your Treasure
            </motion.p>

            <p
              className="text-white text-base text-center px-4 font-normal"
              style={{
                WebkitTextStroke: '0.5px #000',
                textShadow: '0.5px 1px 0px #000',
              }}
            >
              Commander, verifying your chest purchase
            </p>
            <motion.div
              className="flex gap-1 mt-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  className="w-2 h-2 rounded-full shadow-lg"
                  style={{
                    background:
                      'linear-gradient(135deg, #06B6D4 0%, #A855F7 50%, #FFE500 100%)',
                    boxShadow: '0 0 10px rgba(168, 85, 247, 0.6)',
                  }}
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [0.5, 1, 0.5],
                  }}
                  transition={{
                    duration: 1,
                    repeat: Infinity,
                    delay: i * 0.2,
                  }}
                />
              ))}
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};
